import type { FiltersConfig } from '@rocco/ui/filter-sort/types/filter-config-section';

/**
Product filters configuration
Note: The order of filters in this configuration is important because it determines the sequence in which filters are applied and displayed.
This can affect both the user interface and the logic that processes these filters.
 **/
export const PRODUCT_FILTERS_CONFIG: FiltersConfig = {
    resultName: 'Product',
    barFilters: [
        // Order: Colour → Material → Size
        // Staff admin filters are only shown in the modal/drawer (see modalFilters)
        {
            title: 'Colour',
            hideOnSuppliers: true,
            minContentWidth: 20,
            filters: [
                {
                    type: 'color',
                    filterKey: 'colors',
                },
            ],
        },
        {
            title: 'Material',
            hideOnSuppliers: true,
            minContentWidth: 20,
            filters: [
                {
                    type: 'searchable',
                    aggregationProperty: 'aggProductMaterials',
                    filterKey: 'materials',
                    searchInputPlaceholder: 'Search by Material',
                    allowMultipleValues: true,
                    allowAlphabeticalGrouping: true,
                },
            ],
        },
        {
            title: 'Size',
            hideOnSuppliers: true,
            minContentWidth: 15,
            popoverBackground: 'none',
            popoverClassName:
                'before:backdrop-hack before:rounded-4 before:backdrop-blur-[1rem] before:bg-[rgba(224,224,224,0.7)]',
            filters: [
                {
                    type: 'simpleRange',
                    filterKey: 'lengthRange',
                    label: 'Length',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'widthRange',
                    label: 'Width',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'heightRange',
                    label: 'Height',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'diameterRange',
                    label: 'Diameter',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'weightRange',
                    label: 'Weight',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'kg',
                },
            ],
        },
    ],
    modalFilters: [
        {
            title: 'Staff admin filters',
            collapsable: true,
            collapsedByDefault: true,
            adminOnly: true,
            filters: [
                {
                    type: 'default',
                    filterKey: 'invalidLink',
                    label: 'Invalid Link',
                },
                {
                    type: 'default',
                    filterKey: 'disabled',
                    label: 'Disabled',
                },
                {
                    type: 'default',
                    filterKey: 'profileOnly',
                    label: 'Profile Only',
                },
            ],
        },
        {
            title: 'Pricing',
            collapsable: true,
            collapsedByDefault: false,
            filters: [
                {
                    type: 'default',
                    filterKey: 'isPurchasable',
                    label: 'Shop',
                },
                {
                    type: 'default',
                    filterKey: 'isOnSale',
                    label: 'On sale (Discount)',
                },
                {
                    type: 'price',
                    filterKey: 'priceRange',
                    label: 'Price range',
                },
            ],
        },
        {
            title: 'Availability',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'default',
                    filterKey: 'isInStock',
                    label: 'In stock',
                },
                {
                    type: 'default',
                    filterKey: 'isDeliveryAvailable',
                    label: 'Delivery available',
                },
                {
                    type: 'default',
                    filterKey: 'isSampleAvailable',
                    label: 'Samples available',
                },
            ],
        },
        {
            title: 'Downloads',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'default',
                    filterKey: 'downloads',
                },
            ],
        },
        {
            title: 'Colour',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'color',
                    filterKey: 'colors',
                },
            ],
        },
        {
            title: 'Material',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'searchable',
                    aggregationProperty: 'aggProductMaterials',
                    filterKey: 'materials',
                    searchInputPlaceholder: 'Search by Name',
                    allowMultipleValues: true,
                    allowAlphabeticalGrouping: true,
                    columns: 1,
                },
            ],
        },
        {
            title: 'Brand',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'searchable',
                    aggregationProperty: 'aggBrands',
                    filterKey: 'brands',
                    searchInputPlaceholder: 'Search by Brand',
                    allowMultipleValues: true,
                    allowAlphabeticalGrouping: true,
                    columns: 1,
                },
            ],
        },
        {
            title: 'Designer',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'searchable',
                    aggregationProperty: 'aggDesigners',
                    filterKey: 'designers',
                    searchInputPlaceholder: 'Search by Name',
                    allowMultipleValues: true,
                    allowAlphabeticalGrouping: true,
                    columns: 1,
                },
            ],
        },
        {
            title: 'Size',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'simpleRange',
                    filterKey: 'lengthRange',
                    label: 'Length',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'widthRange',
                    label: 'Width',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'heightRange',
                    label: 'Height',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'diameterRange',
                    label: 'Diameter',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'weightRange',
                    label: 'Weight',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'kg',
                },
            ],
        },
        {
            title: 'Suppliers',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'searchable',
                    aggregationProperty: 'aggProfessionals',
                    filterKey: 'professionals',
                    searchInputPlaceholder: 'Search by Name',
                    allowMultipleValues: true,
                    allowAlphabeticalGrouping: true,
                    columns: 1,
                },
            ],
        },
    ],
};
